import { annualTaken, dayLookup, splitHoursOn } from "@core";
import type { EmploymentTerms, LeaveType, Shift } from "@core";

import { supabase } from "./supabase";
import { zonedDate, zonedTime } from "./register";

/**
 * The things waiting on an employer to say yes or no.
 *
 * Three different questions, and only one of them is about money that has not
 * been earned yet:
 *
 *   * **leave** — asked for in advance, granted or not;
 *   * **overtime asked for** — permission to work late on a coming day;
 *   * **overtime already worked** — hours that exist whatever anybody says.
 *
 * The third one is the one to be careful about. Under the BCEA, declining
 * overtime that was worked does not unmake the hours or cancel the debt: the
 * employer is recording that they did not sanction it, which is a matter for a
 * conversation, not a way to pay less. Migration 0022 says so at length and the
 * screen has to say so too, or an employer will press Decline believing it
 * saves them money.
 *
 * Every query is scoped to the household's own employee ids rather than left to
 * RLS. The read policies also show a caller their OWN requests wherever they
 * work, so an account that is a worker somewhere else would otherwise find its
 * own leave sitting in an employer's queue.
 */

export interface PendingLeave {
  id: string;
  employeeId: string;
  employeeName: string;
  type: LeaveType;
  startsOn: string;
  endsOn: string;
  /** What the rules worked out these dates cost. */
  days: number;
  reason: string;
  createdAt: string;
  /**
   * Annual days left before this request is granted, for the warning about a
   * balance going negative. Undefined when the person has no current terms, in
   * which case there is no entitlement to measure against.
   */
  annualLeft?: number;
}

export interface PendingOvertimeAsk {
  id: string;
  employeeId: string;
  employeeName: string;
  onDate: string;
  hours: number;
  reason: string;
  createdAt: string;
}

export interface PendingOvertimeWorked {
  shiftId: string;
  employeeId: string;
  employeeName: string;
  date: string;
  clockIn: string;
  clockOut: string;
  /** Hours beyond the ordinary day, which is what is being answered. */
  overtime: number;
  /** What those hours come to at the overtime rate. Owed either way. */
  amount: number;
}

export interface Queues {
  leave: PendingLeave[];
  asks: PendingOvertimeAsk[];
  worked: PendingOvertimeWorked[];
}

export type DecideResult =
  | { ok: true }
  | { ok: false; reason: "NOT_PERMITTED" | "ALREADY_DECIDED" | "NOT_FOUND" | "NETWORK" };

function nameOf(row: Record<string, any>): string {
  const e = Array.isArray(row.employees) ? row.employees[0] : row.employees;
  return e?.full_name ?? "Unknown";
}

export async function loadQueues(
  employeeIds: string[],
  timeZone: string,
): Promise<Queues> {
  if (employeeIds.length === 0) return { leave: [], asks: [], worked: [] };

  const [leaveRes, askRes, shiftRes, termRes, takenRes] = await Promise.all([
    supabase.from("leave_requests")
      .select("id, employee_id, type, starts_on, ends_on, days, reason, created_at, employees(full_name)")
      .in("employee_id", employeeIds)
      .eq("status", "pending")
      .order("starts_on", { ascending: true }),
    supabase.from("overtime_requests")
      .select("id, employee_id, on_date, hours, reason, created_at, employees(full_name)")
      .in("employee_id", employeeIds)
      .eq("status", "pending")
      .order("on_date", { ascending: true }),
    // Closed shifts nobody has answered. The overtime is worked out from the
    // hours rather than stored, so every closed shift has to be split.
    supabase.from("shifts")
      .select("id, employee_id, terms_id, clock_in, clock_out, employees(full_name)")
      .in("employee_id", employeeIds)
      .is("overtime_status", null)
      .not("clock_out", "is", null)
      .order("clock_in", { ascending: false })
      .limit(200),
    // Every term, not the open one. A shift waiting for an answer may have been
    // worked months ago under a different rate, and pricing it at today's would
    // put a number in front of an employer that was never owed.
    supabase.from("employment_terms").select("*").in("employee_id", employeeIds),
    // Leave already granted, so the screen can say when approving one more
    // would take somebody past their entitlement.
    supabase.from("leave_requests")
      .select("employee_id, type, status, days, days_charged")
      .in("employee_id", employeeIds).eq("status", "approved"),
  ]);

  const termsFor = new Map<string, EmploymentTerms>();
  for (const t of (termRes.data ?? []) as Record<string, any>[]) {
    termsFor.set(t.id, {
      id: t.id,
      employeeId: t.employee_id,
      validFrom: t.valid_from,
      validTo: t.valid_to,
      rate: t.rate_cents / 100,
      currency: t.currency,
      ordinaryDaily: Number(t.ordinary_daily),
      ordinaryWeekly: Number(t.ordinary_weekly),
      breakMins: t.break_mins,
      breakAfter: Number(t.break_after_h),
      ot: Number(t.ot_multiplier),
      sunday: Number(t.sunday_multiplier),
      holiday: Number(t.holiday_multiplier ?? 2),
      daysPerWeek: t.days_per_week,
      annualLeaveDays: Number(t.annual_leave_days),
      sickLeaveDays: Number(t.sick_leave_days),
      sickCycleMonths: t.sick_cycle_months,
      familyLeaveDays: Number(t.family_leave_days),
    });
  }

  // How much annual leave each person has already spent, and what they get.
  const takenBy = new Map<string, { type: string; status: string; days: number; daysCharged?: number }[]>();
  for (const r of (takenRes.data ?? []) as Record<string, any>[]) {
    const list = takenBy.get(r.employee_id) ?? [];
    list.push({
      type: r.type, status: r.status, days: Number(r.days),
      daysCharged: r.days_charged == null ? undefined : Number(r.days_charged),
    });
    takenBy.set(r.employee_id, list);
  }
  const entitlementOf = new Map<string, number>();
  for (const t of termsFor.values()) {
    if (t.validTo === null) entitlementOf.set(t.employeeId, t.annualLeaveDays);
  }

  const leave: PendingLeave[] = ((leaveRes.data ?? []) as Record<string, any>[]).map((r) => ({
    id: r.id,
    employeeId: r.employee_id,
    employeeName: nameOf(r),
    type: r.type,
    startsOn: r.starts_on,
    endsOn: r.ends_on,
    days: Number(r.days),
    reason: r.reason ?? "",
    createdAt: r.created_at,
    annualLeft: entitlementOf.has(r.employee_id)
      ? entitlementOf.get(r.employee_id)! - annualTaken((takenBy.get(r.employee_id) ?? []) as any)
      : undefined,
  }));

  const asks: PendingOvertimeAsk[] = ((askRes.data ?? []) as Record<string, any>[]).map((r) => ({
    id: r.id,
    employeeId: r.employee_id,
    employeeName: nameOf(r),
    onDate: r.on_date,
    hours: Number(r.hours),
    reason: r.reason ?? "",
    createdAt: r.created_at,
  }));

  const dayOf = dayLookup();
  const worked: PendingOvertimeWorked[] = [];
  for (const r of (shiftRes.data ?? []) as Record<string, any>[]) {
    const term = termsFor.get(r.terms_id);
    if (!term) continue;
    const clockIn = Date.parse(r.clock_in);
    const clockOut = Date.parse(r.clock_out);
    const shift = { clockIn, clockOut } as Shift;
    const split = splitHoursOn(shift, term, dayOf(shift));
    // A public holiday has no overtime bucket — s18 prices the whole day — and
    // a Sunday is paid at its multiplier because it is a Sunday, not because
    // somebody worked late. Neither is a decision anybody has to make.
    if (split.overtime <= 0) continue;

    worked.push({
      shiftId: r.id,
      employeeId: r.employee_id,
      employeeName: nameOf(r),
      date: zonedDate(clockIn, timeZone),
      clockIn: zonedTime(clockIn, timeZone),
      clockOut: zonedTime(clockOut, timeZone),
      overtime: split.overtime,
      amount: split.overtime * term.rate * term.ot,
    });
  }

  return { leave, asks, worked };
}

/**
 * A decision on leave, through the RPC rather than an update.
 *
 * decided_by is auth.uid() inside the function and not whatever a client
 * sends. That row is the evidence of who approved somebody's time off, and an
 * approver must not be able to put a colleague's name on their own decision.
 */
export async function decideLeave(
  leaveId: string,
  status: "approved" | "declined",
  /** Days to charge instead of what Shifty computed, and why. */
  override?: { days: number; note: string },
): Promise<DecideResult> {
  const { data, error } = await supabase.rpc("decide_leave", {
    p_leave_id: leaveId,
    p_status: status,
    // Undefined rather than null when nothing was changed, so the argument
    // takes its default and the row records that the figure was accepted.
    p_days: override ? override.days : undefined,
    p_note: override ? override.note : undefined,
  });
  if (error) return { ok: false, reason: error.code === "42501" ? "NOT_PERMITTED" : "NETWORK" };
  if (data === "decided") return { ok: true };
  if (data === "ALREADY_DECIDED") return { ok: false, reason: "ALREADY_DECIDED" };
  if (data === "NOT_FOUND") return { ok: false, reason: "NOT_FOUND" };
  return { ok: false, reason: "NOT_PERMITTED" };
}

export async function decideOvertimeAsk(
  id: string, status: "approved" | "declined",
): Promise<DecideResult> {
  const { data, error } = await supabase
    .from("overtime_requests").update({ status }).eq("id", id).select("id");
  if (error) return { ok: false, reason: error.code === "42501" ? "NOT_PERMITTED" : "NETWORK" };
  return (data ?? []).length > 0 ? { ok: true } : { ok: false, reason: "NOT_PERMITTED" };
}

/**
 * The answer on hours that were already worked.
 *
 * Only two columns are grantable on `shifts` at all — see migration 0023, which
 * narrowed a table-wide UPDATE to overtime_status and overtime_note precisely
 * so that a screen like this one cannot move a clock-in. decided_by and
 * decided_at are stamped by a trigger from the token.
 */
export async function decideOvertimeWorked(
  shiftId: string, status: "approved" | "declined", note = "",
): Promise<DecideResult> {
  const { data, error } = await supabase
    .from("shifts")
    .update({ overtime_status: status, overtime_note: note.trim() || null })
    .eq("id", shiftId)
    .select("id");
  if (error) return { ok: false, reason: error.code === "42501" ? "NOT_PERMITTED" : "NETWORK" };
  // RLS filters rather than refusing, so an empty result means "not yours".
  return (data ?? []).length > 0 ? { ok: true } : { ok: false, reason: "NOT_PERMITTED" };
}
